import { Check, ChevronsUpDown } from "lucide-react";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
	getIssueTypeFilterLabel,
	getIssueTypeFilterSearchValue,
} from "@/types/technicalAudit";

export type IssueType =
	| "title_meta"
	| "h1"
	| "h1_missing"
	| "h1_multiple"
	| "h1_too_long"
	| "alt_text"
	| "redirect"
	| "broken_link"
	| "broken_internal"
	| "canonical"
	| "duplicate_title"
	| "page_title_same_as_h1"
	| "page_title_missing"
	| "page_title_over_60_chars"
	| "page_title_below_30_chars"
	| "duplicate_meta"
	| "meta_missing"
	| "meta_description_over_155_chars"
	| "meta_description_below_70_chars"
	| "thin_content"
	| "indexability"
	| "noindex_important"
	| "slow_response"
	| "near_duplicate"
	| "orphan_page"
	| "schema_error"
	| "missing_schema"
	| "schema_validation_error"
	| "missing_og_tags"
	| "mixed_content"
	| "redirect_loop"
	| "title_too_short"
	| "serp_title_truncated"
	| "serp_meta_truncated"
	| "multiple_meta"
	| "duplicate_h1"
	| "low_readability"
	| "low_text_ratio"
	| "stale_content"
	| "broken_external"
	| "deep_page"
	| "large_image"
	| "broken_image"
	| "non_nextgen_image"
	| "large_page"
	| "unsafe_crossorigin"
	| "nofollow_internal"
	| "low_internal_links"
	| "missing_hsts"
	| "hreflang_error"
	| "sitemap_error"
	| "missing_from_sitemap"
	| "performance_diagnostics"
	| "misspelling"
	| "render_blocking"
	| "links_to_redirect"
	| "links_to_broken"
	| "missing_compression"
	| "bad_url_structure"
	| "has_iframe"
	| "https_to_http"
	| "redirect_chain"
	| "broken_resource"
	| "canonical_to_broken"
	| "canonical_to_redirect"
	| "canonical_chain"
	| "keyword_cannibalization"
	| "image_missing_dimensions"
	| "core_web_vitals";
export type IssueSeverity = "critical" | "warning" | "info";
export type IssueStatus =
	| "pending"
	| "approved"
	| "rejected"
	| "applied"
	| "verified"
	| "failed"
	| "obsolete";

export interface IssueFilters {
	type?: IssueType;
	severity?: IssueSeverity;
	status?: IssueStatus;
	content_type?: "page" | "post" | "product" | "media";
	search?: string;
}

interface IssueTableFiltersProps {
	filters: IssueFilters;
	onFiltersChange: (filters: IssueFilters) => void;
}

const issueTypeLabels: Record<IssueType, string> = {
	title_meta: "Title & Meta",
	h1: getIssueTypeFilterLabel("h1"),
	h1_missing: "Missing H1",
	h1_multiple: "Multiple H1 Tags",
	h1_too_long: "H1 Too Long",
	alt_text: "Image Alt Text",
	redirect: "Redirects",
	broken_link: "Broken Links",
	broken_internal: "Broken Internal URLs",
	canonical: "Canonicals",
	duplicate_title: "Duplicate SEO Title Tags",
	page_title_same_as_h1: "Title Matches H1",
	page_title_missing: "Missing SEO Title",
	page_title_over_60_chars: "SEO Title Too Long",
	page_title_below_30_chars: "SEO Title Too Short",
	duplicate_meta: "Duplicate Descriptions",
	meta_missing: "Missing Meta Description",
	meta_description_over_155_chars: "Meta Description Too Long",
	meta_description_below_70_chars: "Meta Description Too Short",
	thin_content: "Thin Content",
	indexability: "Indexability",
	noindex_important: "Noindex on Important Page",
	slow_response: "Slow Response",
	near_duplicate: "Near-Duplicate Content",
	orphan_page: "Orphan Page",
	schema_error: "Schema Validation Error",
	missing_schema: "Missing Schema",
	schema_validation_error: "Schema Validation Errors",
	missing_og_tags: "Missing Open Graph Tags",
	mixed_content: "Mixed Content",
	redirect_loop: "Redirect Loop",
	title_too_short: "Title Too Short",
	serp_title_truncated: "SERP Title Truncated",
	serp_meta_truncated: "SERP Description Truncated",
	multiple_meta: "Multiple Meta Descriptions",
	duplicate_h1: "Duplicate H1",
	low_readability: "Low Readability",
	low_text_ratio: "Low Text-to-HTML Ratio",
	stale_content: "Stale Content",
	broken_external: "Broken External Link",
	deep_page: "Deep Page",
	large_image: "Oversized Image",
	broken_image: "Broken Image",
	non_nextgen_image: "Non-Next-Gen Image",
	large_page: "Oversized Page",
	unsafe_crossorigin: "Unsafe Cross-Origin Link",
	nofollow_internal: "Nofollow Internal Link",
	low_internal_links: "Low Internal Links",
	missing_hsts: "Missing HSTS",
	hreflang_error: "Hreflang Error",
	sitemap_error: "Sitemap Issue",
	missing_from_sitemap: "Missing from Sitemap",
	performance_diagnostics: "Performance Diagnostics",
	misspelling: "Spelling Errors",
	render_blocking: "Render-Blocking Resources",
	links_to_redirect: "Links to Redirects",
	links_to_broken: "Links to Broken Page",
	missing_compression: "Missing Compression",
	bad_url_structure: "Non-SEO-Friendly URL",
	has_iframe: "Contains iFrame",
	https_to_http: "HTTPS to HTTP Link",
	redirect_chain: "Redirect Chain",
	broken_resource: "Broken Resource",
	canonical_to_broken: "Canonical Points to Broken",
	canonical_to_redirect: "Canonical Points to Redirect",
	canonical_chain: "Canonical Chain",
	keyword_cannibalization: "Keyword Cannibalization",
	image_missing_dimensions: "Image Missing Dimensions",
	core_web_vitals: "Core Web Vitals",
};

const severityLabels: Record<IssueSeverity, string> = {
	critical: "Critical",
	warning: "Warning",
	info: "Info",
};

const statusLabels: Record<IssueStatus, string> = {
	pending: "Pending",
	approved: "Suggested",
	rejected: "Skipped",
	applied: "Applied",
	verified: "Verified",
	failed: "Failed",
	obsolete: "Obsolete",
};

export function IssueTableFilters({
	filters,
	onFiltersChange,
}: IssueTableFiltersProps) {
	const [issueTypeOpen, setIssueTypeOpen] = useState(false);
	const sortedIssueTypes = useMemo(
		() =>
			(Object.entries(issueTypeLabels) as [IssueType, string][]).sort((a, b) =>
				a[1].localeCompare(b[1]),
			),
		[],
	);

	const handleTypeChange = (value: string) => {
		onFiltersChange({
			...filters,
			type: value === "all" ? undefined : (value as IssueType),
		});
	};

	const handleSeverityChange = (value: string) => {
		onFiltersChange({
			...filters,
			severity: value === "all" ? undefined : (value as IssueSeverity),
		});
	};

	const handleStatusChange = (value: string) => {
		onFiltersChange({
			...filters,
			status: value === "all" ? undefined : (value as IssueStatus),
		});
	};

	return (
		<div className="flex flex-wrap gap-4 mb-4">
			<Popover open={issueTypeOpen} onOpenChange={setIssueTypeOpen}>
				<PopoverTrigger asChild>
					<Button
						type="button"
						variant="outline"
						role="combobox"
						aria-expanded={issueTypeOpen}
						className="w-[220px] justify-between"
					>
						<span className="truncate">
							{filters.type ? issueTypeLabels[filters.type] : "All issue types"}
						</span>
						<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-[260px] p-0" align="start">
					<Command>
						<CommandInput placeholder="Search issue types..." />
						<CommandList>
							<CommandEmpty>No issue type found.</CommandEmpty>
							<CommandGroup>
								<CommandItem
									value="all issue types"
									onSelect={() => {
										handleTypeChange("all");
										setIssueTypeOpen(false);
									}}
								>
									<Check
										className={cn(
											"mr-2 h-4 w-4",
											!filters.type ? "opacity-100" : "opacity-0",
										)}
									/>
									All issue types
								</CommandItem>
								{sortedIssueTypes.map(([value, label]) => (
									<CommandItem
										key={value}
										value={getIssueTypeFilterSearchValue(value)}
										onSelect={() => {
											handleTypeChange(value);
											setIssueTypeOpen(false);
										}}
									>
										<Check
											className={cn(
												"mr-2 h-4 w-4",
												filters.type === value ? "opacity-100" : "opacity-0",
											)}
										/>
										{label}
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			<Select
				value={filters.severity || "all"}
				onValueChange={handleSeverityChange}
			>
				<SelectTrigger className="w-[150px]">
					<SelectValue placeholder="Severity" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">All Severities</SelectItem>
					{Object.entries(severityLabels).map(([value, label]) => (
						<SelectItem key={value} value={value}>
							{label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			<Select
				value={filters.status || "all"}
				onValueChange={handleStatusChange}
			>
				<SelectTrigger className="w-[150px]">
					<SelectValue placeholder="Status" />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">All Statuses</SelectItem>
					{Object.entries(statusLabels).map(([value, label]) => (
						<SelectItem key={value} value={value}>
							{label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

		</div>
	);
}
